// Gói liệu trình: danh mục trên máy chủ (API /api/v2/treatment) — tạo / sửa gói, dịch vụ trong gói (chính / tặng),
// giá chi nhánh, khuyến mãi, hoàn tiền, lịch sử giá, bật/tắt bán, xuất bảng giá, quy tắc chung
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DatePicker, Drawer, Empty, Input, InputNumber, Modal, Segmented, Select, Spin, Switch, Table, Tabs, Timeline, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Download, Plus, Settings2, Trash2 } from 'lucide-react';
import MoneyInput from '../POS/components/MoneyInput';
import { getServices } from '../../api/Service';
import { getBranches } from '../../api/Branch';
import {
  createTreatmentPackage, exportTreatmentPackages, getTreatmentConfig, getTreatmentPackage, getTreatmentPackagePriceHistory,
  getTreatmentPackages, setTreatmentPackageActive, updateTreatmentConfig, updateTreatmentPackage,
  type PackageBody, type PackageDetail, type PackageItemRole, type PackageKind, type PackageListItem, type PackagePromo,
  type PriceHistoryItem, type PromoState, type TreatmentConfigDto, type TreatmentResult,
} from '../../api/TreatmentV2';
import { KIND_LABEL, ROLE_LABEL } from './data';
import { round1000, ymd } from './logic';
import { useTreatmentAuth } from './actions';
import TreatmentShell, { Pill, fmtDate, fmtDateTime, money } from './ui';

const PROMO_PILL: Record<PromoState, { label: string; cls: string }> = {
  running: { label: 'Đang chạy', cls: 'bg-green-50 text-green-700 border-green-200' },
  upcoming: { label: 'Sắp tới', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  ended: { label: 'Đã hết', cls: 'bg-slate-50 text-slate-500 border-slate-200' },
};
const promoStateOf = (promo: PackagePromo | null): PromoState | null => {
  if (!promo) return null;
  const today = ymd();
  return today < promo.from ? 'upcoming' : today > promo.to ? 'ended' : 'running';
};

const Label = ({ children, required }: { children: React.ReactNode; required?: boolean }) => (
  <span className="mb-1 block text-sm font-medium text-slate-700">{children}{required && <span className="text-red-500"> *</span>}</span>
);
const Section = ({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) => (
  <div className="rounded-xl border border-slate-200 p-4">
    <div className="mb-3"><div className="font-semibold text-slate-800">{title}</div>{hint && <div className="text-xs text-slate-500">{hint}</div>}</div>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// Danh mục dịch vụ đang hoạt động và chi nhánh (id số thật), tải một lần cho cả màn

interface Svc { id: number; name: string; price: number }
interface Branch { id: number; name: string }

let catalogCache: Promise<{ services: Svc[]; branches: Branch[] }> | null = null;
const loadCatalog = () => {
  if (catalogCache) return catalogCache;
  catalogCache = Promise.all([getServices({ status: 0, take: 1000 }), getBranches({ status: 0 })]).then(([s, b]: [any, any]) => ({
    services: (s?.data?.items || []).map((x: any) => ({ id: Number(x.id), name: x.name, price: Number(x.price) || 0 })),
    branches: (b?.data?.items || b?.data || []).map((x: any) => ({ id: Number(x.id), name: x.name })),
  })).catch(() => { catalogCache = null; return { services: [], branches: [] }; });
  return catalogCache;
};
function useCatalog() {
  const [cat, setCat] = useState<{ services: Svc[]; branches: Branch[] }>({ services: [], branches: [] });
  useEffect(() => { let alive = true; loadCatalog().then(c => { if (alive) setCat(c); }); return () => { alive = false; }; }, []);
  return cat;
}

// ---------------------------------------------------------------------------
// Form gói: thông tin & dịch vụ · quy định tiền · lịch sử giá

interface FormItem { service_id: number | undefined; sessions: number; role: PackageItemRole }
interface PackageFormState {
  id?: number;
  code: string;
  name: string;
  kind: PackageKind;
  items: FormItem[];
  face_value: number;
  price: number;
  validity_months: number;
  /** branch_id → giá riêng (0 = theo niêm yết) */
  branchPrices: Record<number, number>;
  promo: PackagePromo | null;
  allow_refund: boolean;
  refund_fee_percent: number;
  is_active: boolean;
  note: string;
  sold_count: number;
}

const emptyForm = (): PackageFormState => ({
  code: '', name: '', kind: 'session', items: [{ service_id: undefined, sessions: 5, role: 'main' }], face_value: 0, price: 0, validity_months: 6,
  branchPrices: {}, promo: null, allow_refund: true, refund_fee_percent: 10, is_active: true, note: '', sold_count: 0,
});

const fromDetail = (d: PackageDetail): PackageFormState => ({
  id: d.id, code: d.code, name: d.name, kind: d.kind,
  items: d.items.map(i => ({ service_id: i.service_id, sessions: i.sessions, role: i.role })),
  face_value: Number(d.face_value) || 0, price: Number(d.price) || 0, validity_months: d.validity_months,
  branchPrices: Object.fromEntries((d.branch_prices || []).filter(b => b.price).map(b => [b.branch_id, Number(b.price)])),
  promo: d.promo ? { ...d.promo, price: Number(d.promo.price), label: d.promo.label || '' } : null,
  allow_refund: d.allow_refund, refund_fee_percent: Number(d.refund_fee_percent) || 0, is_active: d.is_active, note: d.note || '', sold_count: d.sold_count || 0,
});

const toBody = (f: PackageFormState): PackageBody => ({
  code: f.code.trim().toUpperCase(), name: f.name.trim(), kind: f.kind,
  items: f.items.filter(i => i.service_id && (f.kind === 'value' || i.sessions > 0))
    .map(i => ({ service_id: i.service_id!, sessions: f.kind === 'value' ? 0 : i.sessions, role: f.kind === 'value' ? 'main' : i.role })),
  price: f.price, ...(f.kind === 'value' ? { face_value: f.face_value } : {}), validity_months: f.validity_months,
  branch_prices: Object.entries(f.branchPrices).filter(([, v]) => v > 0).map(([k, v]) => ({ branch_id: Number(k), price: v })),
  promo: f.promo, allow_refund: f.allow_refund, refund_fee_percent: f.allow_refund ? f.refund_fee_percent : 0, is_active: f.is_active, note: f.note.trim(),
});

/** Giá trị/buổi xem trước khi đang gõ (cùng công thức backend); hiển thị sau khi lưu lấy từ máy chủ */
const previewUnitValue = (f: PackageFormState, svc: (id?: number) => Svc | undefined) => {
  const main = f.items.filter(i => i.role === 'main' && i.sessions > 0);
  const weights = main.map(i => (svc(i.service_id)?.price || 0) * i.sessions);
  const total = weights.reduce((s, w) => s + w, 0);
  const useSessions = total === 0;
  const sum = useSessions ? main.reduce((s, i) => s + i.sessions, 0) : total;
  return (it: FormItem) => {
    if (it.role !== 'main' || it.sessions <= 0 || !sum) return 0;
    const w = useSessions ? it.sessions : (svc(it.service_id)?.price || 0) * it.sessions;
    return Math.round(f.price * w / sum / it.sessions);
  };
};

/** Mã lỗi backend → tab cần mở */
const INFO_CODES = ['PACKAGE_CODE_REQUIRED', 'PACKAGE_CODE_EXISTS', 'PACKAGE_NAME_REQUIRED', 'PACKAGE_MAIN_SERVICE_REQUIRED', 'PACKAGE_DUPLICATE_SERVICE', 'SERVICE_NOT_FOUND', 'PACKAGE_FACE_VALUE_INVALID', 'PACKAGE_KIND_LOCKED'];
const PRICING_CODES = ['PACKAGE_PRICE_REQUIRED', 'PACKAGE_PROMO_INVALID', 'PACKAGE_REFUND_FEE_INVALID'];
const tabOfError = (r: TreatmentResult<unknown>) => {
  if (r.code && INFO_CODES.includes(r.code)) return 'info';
  if (r.code && PRICING_CODES.includes(r.code)) return 'pricing';
  const keys = Object.keys(r.errors || {});
  return keys.some(k => /^(price|branch_prices|promo|allow_refund|refund_fee)/.test(k)) && !keys.some(k => /^(code|name|items|kind|face_value|validity)/.test(k)) ? 'pricing' : 'info';
};

function PackageForm({ target, onClose, onSaved }: { target: { id?: number } | null; onClose: () => void; onSaved: () => void }) {
  const { services, branches } = useCatalog();
  const [f, setF] = useState<PackageFormState | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('info');
  const [history, setHistory] = useState<PriceHistoryItem[] | null>(null);
  const [serverErr, setServerErr] = useState<{ tab: string; texts: string[] } | null>(null);

  const svcMap = useMemo(() => new Map(services.map(s => [s.id, s])), [services]);
  const svc = useCallback((id?: number) => (id ? svcMap.get(id) : undefined), [svcMap]);

  const loadHistory = (id: number) => { getTreatmentPackagePriceHistory(id).then(r => setHistory(r.ok ? r.data || [] : [])); };

  useEffect(() => {
    setTab('info'); setServerErr(null); setHistory(null);
    if (!target) { setF(null); return; }
    if (!target.id) { setF(emptyForm()); return; }
    let alive = true;
    setLoading(true);
    getTreatmentPackage(target.id).then(r => {
      if (!alive) return;
      setLoading(false);
      if (!r.ok || !r.data) { message.error(r.message || 'Không tải được gói'); onClose(); return; }
      setF(fromDetail(r.data));
      loadHistory(target.id!);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const open = !!target;
  if (!f) {
    return (
      <Drawer open={open} onClose={onClose} width={720} title="Gói liệu trình" destroyOnHidden>
        {loading && <div className="flex justify-center py-16"><Spin /></div>}
      </Drawer>
    );
  }

  const isNew = !f.id;
  const kindLocked = !isNew && f.sold_count > 0;
  const set = (patch: Partial<PackageFormState>) => { setF({ ...f, ...patch }); setServerErr(null); };
  const setItem = (i: number, patch: Partial<FormItem>) => set({ items: f.items.map((x, k) => (k === i ? { ...x, ...patch } : x)) });

  const listValue = f.items.filter(i => i.role === 'main').reduce((s, i) => s + (svc(i.service_id)?.price || 0) * i.sessions, 0);
  const bonusValue = f.items.filter(i => i.role === 'bonus').reduce((s, i) => s + (svc(i.service_id)?.price || 0) * i.sessions, 0);
  const unitValue = previewUnitValue(f, svc);

  // Kiểm tra nhanh trước khi gửi; backend vẫn là nơi kiểm tra cuối (mã gói trùng, dịch vụ ngưng...)
  const errors: { tab: string; text: string }[] = [];
  const err = (tabKey: string, text: string) => errors.push({ tab: tabKey, text });
  if (!f.code.trim()) err('info', 'Nhập mã gói');
  if (!f.name.trim()) err('info', 'Nhập tên gói');
  if (f.kind === 'session' && !f.items.some(i => i.role === 'main' && i.service_id && i.sessions > 0)) err('info', 'Gói cần ít nhất 1 dịch vụ chính');
  if (f.kind === 'session' && f.items.some(i => !i.service_id)) err('info', 'Chọn dịch vụ cho từng dòng');
  const ids = f.items.map(i => i.service_id).filter(Boolean);
  if (new Set(ids).size !== ids.length) err('info', 'Mỗi dịch vụ chỉ xuất hiện 1 lần');
  if (f.price <= 0) err('pricing', 'Nhập giá niêm yết');
  if (f.kind === 'value' && f.face_value < f.price) err('info', 'Mệnh giá phải ≥ giá bán');
  if (f.promo) {
    if (f.promo.price <= 0 || f.promo.price >= f.price) err('pricing', 'Giá khuyến mãi phải nhỏ hơn giá niêm yết');
    if (!f.promo.from || !f.promo.to || f.promo.from > f.promo.to) err('pricing', 'Chọn thời gian khuyến mãi hợp lệ');
  }

  const save = async () => {
    if (errors.length) { setTab(errors[0].tab); return; }
    setSaving(true);
    const body = toBody(f);
    const r = isNew ? await createTreatmentPackage(body) : await updateTreatmentPackage(f.id!, body);
    setSaving(false);
    if (!r.ok || !r.data) {
      if (r.code === 'FORBIDDEN') { message.error(r.message || 'Bạn không có quyền sửa gói'); return; }
      const t = tabOfError(r);
      const texts = Array.from(new Set([r.message, ...Object.values(r.errors || {}).flat()].filter(Boolean)));
      setServerErr({ tab: t, texts });
      setTab(t);
      return;
    }
    message.success(isNew ? 'Đã tạo gói – gói xuất hiện ngay trong danh mục POS' : r.data.history_added ? 'Đã lưu gói và ghi lịch sử giá' : 'Đã lưu gói');
    onSaved();
    onClose();
  };

  const ps = promoStateOf(f.promo);

  const infoTab = (
    <div className="space-y-4 text-sm">
      <Tooltip title={kindLocked ? 'Gói đã bán thẻ, không đổi được loại thẻ' : ''}>
        <Segmented block value={f.kind} disabled={kindLocked}
          onChange={v => set({ kind: v as PackageKind, items: v === 'value' ? [] : emptyForm().items })}
          options={[{ value: 'session', label: 'Thẻ buổi (trừ theo buổi)' }, { value: 'value', label: 'Thẻ giá trị (trừ theo tiền)' }]} />
      </Tooltip>
      <label className="block"><Label required>Mã gói</Label><Input value={f.code} maxLength={30} onChange={e => set({ code: e.target.value.toUpperCase().replace(/\s/g, '') })} placeholder="VD: GOI-HIFU5" /></label>
      <label className="block"><Label required>Tên gói</Label><Input value={f.name} maxLength={120} onChange={e => set({ name: e.target.value })} placeholder="VD: Gói Hifu 5 buổi" /></label>

      {f.kind === 'session' ? (
        <div>
          <Label required>Dịch vụ trong gói</Label>
          {/* Cột cố định độ rộng, tên dịch vụ dài thì cắt "…"; màn hẹp thì cuộn ngang trong khung */}
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[560px] table-fixed">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr><th className="px-2 py-1.5 text-left">Dịch vụ</th><th className="w-20 px-2 py-1.5">Số buổi</th><th className="w-32 px-2 py-1.5">Loại</th><th className="w-28 whitespace-nowrap px-2 py-1.5 text-right">Giá trị/buổi</th><th className="w-9" /></tr>
              </thead>
              <tbody>
                {f.items.map((it, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-2 py-1.5"><Select size="small" className="w-full min-w-0" showSearch optionFilterProp="label" placeholder="Chọn dịch vụ" value={it.service_id} onChange={v => setItem(i, { service_id: v })}
                      popupMatchSelectWidth={false} styles={{ popup: { root: { maxWidth: 480 } } }}
                      options={services.map(s => ({ value: s.id, label: `${s.name} · ${money(s.price)}` }))} /></td>
                    <td className="px-2 py-1.5"><InputNumber size="small" className="!w-full" min={1} max={100} value={it.sessions} onChange={v => setItem(i, { sessions: Number(v) || 1 })} /></td>
                    <td className="px-2 py-1.5"><Segmented size="small" value={it.role} onChange={v => setItem(i, { role: v as PackageItemRole })} options={[{ value: 'main', label: 'Chính' }, { value: 'bonus', label: 'Tặng' }]} /></td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-right text-xs text-slate-600">{unitValue(it) ? money(unitValue(it)) : '—'}</td>
                    <td className="px-1">{f.items.length > 1 && <button type="button" onClick={() => set({ items: f.items.filter((_, k) => k !== i) })} className="p-1 text-slate-400 hover:text-red-500" aria-label="Xóa dịch vụ"><Trash2 className="h-4 w-4" /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={() => set({ items: [...f.items, { service_id: undefined, sessions: 1, role: 'bonus' }] })}
            className="mt-2 flex items-center gap-1 text-sm font-semibold text-[#2c6b7f]"><Plus className="h-4 w-4" /> Thêm dịch vụ</button>
          <p className="mt-1 text-xs text-slate-500">Dịch vụ <b>chính</b> được phân bổ giá niêm yết theo giá lẻ × số buổi (dùng tính hoàn tiền, ghi nhận doanh thu theo buổi). Dịch vụ <b>tặng</b> có giá trị 0, không được hoàn tiền.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <label><Label required>Mệnh giá sử dụng</Label><MoneyInput value={f.face_value} onChange={v => set({ face_value: v })} className="!h-8 !text-sm" /></label>
          <label><Label>Dịch vụ áp dụng</Label><Select mode="multiple" className="w-full" placeholder="Bỏ trống = mọi dịch vụ" showSearch optionFilterProp="label"
            value={f.items.map(i => i.service_id).filter(Boolean) as number[]}
            onChange={(v: number[]) => set({ items: v.map(id => ({ service_id: id, sessions: 0, role: 'main' as const })) })}
            options={services.map(s => ({ value: s.id, label: s.name }))} /></label>
        </div>
      )}
      <label className="block w-1/3"><Label>Hạn dùng (tháng)</Label><InputNumber className="w-full" min={1} max={60} value={f.validity_months} onChange={v => set({ validity_months: Number(v) || 1 })} /></label>
      <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
        {f.kind === 'session'
          ? <>Giá lẻ dịch vụ chính {money(listValue)}đ{bonusValue > 0 && ` + tặng ${money(bonusValue)}đ`}, giá niêm yết {money(f.price)}đ → khách tiết kiệm <b>{money(Math.max(0, listValue + bonusValue - f.price))}đ</b>{listValue + bonusValue > 0 && ` (${Math.round(Math.max(0, listValue + bonusValue - f.price) / (listValue + bonusValue) * 100)}%)`}.</>
          : <>Khách trả {money(f.price)}đ, được dùng {money(f.face_value)}đ → ưu đãi <b>{f.price > 0 ? Math.round((f.face_value - f.price) / f.price * 100) : 0}%</b>.</>}
      </div>
      <label className="block"><Label>Ghi chú</Label><Input.TextArea rows={2} maxLength={300} value={f.note} onChange={e => set({ note: e.target.value })} placeholder="Điều kiện áp dụng, đối tượng khách…" /></label>
      <label className="flex items-center gap-2"><Switch checked={f.is_active} onChange={v => set({ is_active: v })} /> Đang bán (hiện trong danh mục POS)</label>
    </div>
  );

  const pricingTab = (
    <div className="space-y-4 text-sm">
      <Section title="Giá bán" hint="Giá niêm yết dùng chung; chi nhánh có thể đặt giá riêng (bỏ trống = theo niêm yết).">
        <div className="grid grid-cols-2 gap-3">
          <label><Label required>Giá niêm yết</Label><MoneyInput value={f.price} onChange={v => set({ price: v })} className="!h-9 !text-sm" /></label>
          <div />
          {branches.map(b => {
            const v = f.branchPrices[b.id] || 0;
            const diff = v && f.price ? Math.round((v - f.price) / f.price * 100) : 0;
            return (
              <label key={b.id}>
                <Label>Giá tại {b.name} <span className="font-normal text-slate-400">(0 = theo niêm yết)</span></Label>
                <MoneyInput value={v} onChange={x => set({ branchPrices: { ...f.branchPrices, [b.id]: x } })} className="!h-9 !text-sm" />
                {v > 0 && <span className={`text-xs ${diff > 0 ? 'text-amber-700' : diff < 0 ? 'text-green-700' : 'text-slate-500'}`}>{diff > 0 ? '+' : ''}{diff}% so với niêm yết</span>}
              </label>
            );
          })}
        </div>
      </Section>

      <Section title="Khuyến mãi theo thời gian" hint="Trong thời gian khuyến mãi, giá bán = giá khuyến mãi (nếu thấp hơn giá chi nhánh).">
        <label className="mb-3 flex items-center gap-2">
          <Switch checked={!!f.promo} onChange={v => set({ promo: v ? { price: round1000(f.price * 0.9), from: ymd(), to: dayjs().add(30, 'day').format('YYYY-MM-DD'), label: 'Khuyến mãi' } : null })} />
          Có khuyến mãi {ps && <Pill {...PROMO_PILL[ps]} />}
        </label>
        {f.promo && (
          <div className="grid grid-cols-2 gap-3">
            <label><Label required>Giá khuyến mãi</Label><MoneyInput value={f.promo.price} onChange={v => set({ promo: { ...f.promo!, price: v } })} className="!h-9 !text-sm" invalid={f.promo.price >= f.price} /></label>
            <label><Label>Tên chương trình</Label><Input value={f.promo.label || ''} maxLength={60} onChange={e => set({ promo: { ...f.promo!, label: e.target.value } })} /></label>
            <label className="col-span-2"><Label required>Thời gian</Label>
              <DatePicker.RangePicker className="w-full" format="DD/MM/YYYY" allowClear={false} value={[dayjs(f.promo.from), dayjs(f.promo.to)]}
                onChange={r => r && r[0] && r[1] && set({ promo: { ...f.promo!, from: r[0].format('YYYY-MM-DD'), to: r[1].format('YYYY-MM-DD') } })} />
            </label>
            {f.price > 0 && <div className="col-span-2 text-xs text-green-700">Giảm {money(f.price - f.promo.price)}đ ({Math.round((f.price - f.promo.price) / f.price * 100)}%) so với niêm yết</div>}
          </div>
        )}
      </Section>

      <Section title="Hoàn tiền">
        <label className="mb-3 flex items-center gap-2"><Switch checked={f.allow_refund} onChange={v => set({ allow_refund: v })} /> Cho phép hoàn tiền</label>
        <label className="flex items-center justify-between gap-3">
          <span>Phí hoàn tiền (trên số được hoàn)</span>
          <InputNumber className="w-36" min={0} max={100} disabled={!f.allow_refund} value={f.refund_fee_percent} onChange={v => set({ refund_fee_percent: Number(v) || 0 })} addonAfter="%" />
        </label>
        <p className="mt-1 text-xs text-slate-500">Số được hoàn = đã đóng − giá trị đã dùng (theo giá trị/buổi ở tab Thông tin). Không cho hoàn thì chỉ quản lý được hoàn ngoại lệ.</p>
      </Section>
      <p className="text-xs text-slate-500">Trả góp và chiết khấu khi bán theo quy tắc chung của POS. Quy định tiền được chụp vào thẻ lúc bán: đổi quy định sau này không ảnh hưởng thẻ đã bán.</p>
    </div>
  );

  const historyTab = isNew ? <Empty description="Lưu gói xong sẽ có lịch sử giá" />
    : history === null ? <div className="flex justify-center py-10"><Spin /></div>
      : history.length ? (
        <Timeline className="!mt-2" items={history.map(h => ({
          children: (
            <div className="text-sm">
              <div className="text-xs text-slate-400">{fmtDateTime(h.changed_at)} · {h.changed_by_name || '—'}</div>
              <ul className="list-disc pl-4">{h.changes.map(c => <li key={c}>{c}</li>)}</ul>
            </div>
          ),
        }))} />
      ) : <Empty description="Chưa có thay đổi giá" />;

  const errTabs = new Set([...errors.map(e => e.tab), ...(serverErr ? [serverErr.tab] : [])]);
  const shownErrors = [...errors.map(e => e.text), ...(serverErr?.texts || [])];
  return (
    <Drawer open={open} onClose={onClose} width={720} title={isNew ? 'Tạo gói liệu trình' : `Sửa gói ${f.code}`} destroyOnHidden
      extra={<button onClick={save} disabled={saving} className="h-9 rounded-lg bg-[#357f96] px-4 text-sm font-semibold text-white disabled:opacity-40">{saving ? 'Đang lưu…' : 'Lưu gói'}</button>}>
      <Tabs activeKey={tab} onChange={setTab} items={[
        { key: 'info', label: <span>Thông tin & dịch vụ{errTabs.has('info') && <span className="ml-1 text-red-500">•</span>}</span>, children: infoTab },
        { key: 'pricing', label: <span>Quy định tiền{errTabs.has('pricing') && <span className="ml-1 text-red-500">•</span>}</span>, children: pricingTab },
        { key: 'history', label: `Lịch sử giá${history?.length ? ` (${history.length})` : ''}`, children: historyTab },
      ]} />
      {shownErrors.length > 0 && <ul className="mt-3 list-disc pl-4 text-xs text-red-600">{shownErrors.map(t => <li key={t}>{t}</li>)}</ul>}
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Quy tắc chung của module (lưu trên máy chủ, cấu hình chung — branch_id null)

type ConfigForm = Omit<TreatmentConfigDto, 'branch_id' | 'is_branch_override'>;

function ConfigModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [c, setC] = useState<ConfigForm | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setC(null);
    getTreatmentConfig().then(r => {
      if (r.ok && r.data) {
        const d = r.data;
        setC({
          low_sessions: d.low_sessions, low_value_percent: d.low_value_percent, expiring_days: d.expiring_days, due_soon_days: d.due_soon_days,
          block_overdue: d.block_overdue, limit_by_paid: d.limit_by_paid, cancel_usage_hours: d.cancel_usage_hours,
          refund_fee_percent: Number(d.refund_fee_percent), refund_approval_threshold: Number(d.refund_approval_threshold),
        });
      }
      else { message.error(r.message || 'Không tải được quy tắc chung'); onClose(); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const save = async () => {
    if (!c) return;
    setSaving(true);
    const r = await updateTreatmentConfig({ branch_id: null, ...c });
    setSaving(false);
    if (!r.ok) { message.error(r.message || 'Không lưu được quy tắc'); return; }
    message.success('Đã lưu quy tắc');
    onClose();
  };
  const num = (k: keyof ConfigForm, label: string, suffix: string, max = 365) => (
    <label className="flex items-center justify-between gap-3">
      <span>{label}</span>
      <InputNumber className="w-32" min={0} max={max} value={c![k] as number} onChange={v => setC({ ...c!, [k]: Number(v) || 0 })} addonAfter={suffix} />
    </label>
  );
  return (
    <Modal open={open} onCancel={onClose} title="Quy tắc chung thẻ liệu trình" centered okText="Lưu" cancelText="Hủy" width={520}
      onOk={save} confirmLoading={saving} okButtonProps={{ disabled: !c }}>
      {!c ? <div className="flex justify-center py-10"><Spin /></div> : (
        <div className="space-y-3 text-sm">
          <p className="text-xs text-slate-500">Giá, khuyến mãi và phí hoàn đặt trong từng gói. Trả góp và chiết khấu khi bán theo quy tắc chung của POS.</p>
          <div className="font-semibold text-slate-700">Cảnh báo</div>
          {num('low_sessions', 'Sắp hết thẻ khi còn ≤', 'buổi', 20)}
          {num('low_value_percent', 'Thẻ giá trị sắp hết khi còn ≤', '%', 100)}
          {num('expiring_days', 'Báo sắp hết hạn trước', 'ngày')}
          {num('due_soon_days', 'Nhắc đợt thanh toán trước', 'ngày')}
          <div className="pt-2 font-semibold text-slate-700">Trừ buổi</div>
          <label className="flex items-center justify-between gap-3"><span>Có đợt nợ quá hạn thì cần quản lý duyệt</span><Switch checked={c.block_overdue} onChange={v => setC({ ...c, block_overdue: v })} /></label>
          <label className="flex items-center justify-between gap-3"><span>Không cho dùng vượt số tiền đã đóng (cần duyệt)</span><Switch checked={c.limit_by_paid} onChange={v => setC({ ...c, limit_by_paid: v })} /></label>
          {num('cancel_usage_hours', 'Được hủy lượt trừ trong', 'giờ', 720)}
          <div className="pt-2 font-semibold text-slate-700">Hoàn tiền</div>
          {num('refund_fee_percent', 'Phí hoàn mặc định (thẻ chuyển đổi)', '%', 100)}
          <label className="flex items-center justify-between gap-3">
            <span>Phiếu hoàn (thẻ chuyển đổi) cần duyệt từ</span>
            <span className="w-40"><MoneyInput value={c.refund_approval_threshold} onChange={v => setC({ ...c, refund_approval_threshold: v })} className="!h-8 !text-sm" /></span>
          </label>
          <p className="text-xs text-slate-500">Thẻ bán qua POS dùng ngưỡng duyệt hoàn tiền của POS.</p>
        </div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------

const PAGE_SIZE = 20;

const itemsSummary = (p: PackageListItem) => (p.kind === 'value'
  ? (p.items.length ? `Áp dụng: ${p.items.map(i => i.service_name).join(', ')}` : 'Mọi dịch vụ')
  : p.items.map(i => `${i.service_name || `#${i.service_id}`} ×${i.sessions}${i.role === 'bonus' ? ' (tặng)' : ''}`).join(' · '));

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export default function TreatmentPackages() {
  const { can } = useTreatmentAuth();
  const { branches } = useCatalog();
  const [view, setView] = useState<'list' | 'pricing'>('list');
  const [editing, setEditing] = useState<{ id?: number } | null>(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<PackageListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ active: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const reqId = useRef(0);

  // Gõ tìm kiếm: chờ 300ms rồi mới gọi máy chủ
  useEffect(() => { const t = setTimeout(() => { setSearch(q.trim()); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    const r = await getTreatmentPackages({ search, page, per_page: PAGE_SIZE, include: view === 'pricing' ? 'branch_prices' : undefined });
    if (id !== reqId.current) return;
    setLoading(false);
    if (!r.ok || !r.data) { message.error(r.message || 'Không tải được danh sách gói'); return; }
    setRows(r.data.items);
    setTotal(r.data.pagination.total);
    setSummary(r.data.summary);
  }, [search, page, view]);

  useEffect(() => { load(); }, [load]);

  // Công tắc Đang bán: đổi ngay, lỗi thì trả lại
  const toggle = async (p: PackageListItem) => {
    const next = !p.is_active;
    setRows(rs => rs.map(x => (x.id === p.id ? { ...x, is_active: next } : x)));
    setSummary(s => ({ ...s, active: s.active + (next ? 1 : -1) }));
    const r = await setTreatmentPackageActive(p.id, next);
    if (!r.ok) {
      setRows(rs => rs.map(x => (x.id === p.id ? { ...x, is_active: !next } : x)));
      setSummary(s => ({ ...s, active: s.active + (next ? -1 : 1) }));
      message.error(r.message || 'Không đổi được trạng thái bán');
    }
  };

  const exportPricing = async () => {
    setExporting(true);
    const r = await exportTreatmentPackages({ search: search || undefined });
    setExporting(false);
    if (!r.ok || !r.blob) { message.error(r.message); return; }
    downloadBlob(r.blob, r.filename || `bang-gia-goi-lieu-trinh-${ymd()}.xlsx`);
  };

  const priceCell = (p: PackageListItem) => {
    const cp = p.current_price;
    return (
      <span>
        <b>{money(cp.price)}</b>
        {cp.price !== Number(p.price) && <span className="ml-1 text-xs text-slate-400 line-through">{money(p.price)}</span>}
        <div className="mt-0.5 flex flex-wrap gap-1">
          {cp.source === 'promo' && <Pill label={cp.promo_label || 'Khuyến mãi'} cls="bg-green-50 text-green-700 border-green-200" />}
          {p.has_branch_prices && <Pill label="Giá riêng chi nhánh" cls="bg-blue-50 text-blue-700 border-blue-200" />}
        </div>
      </span>
    );
  };

  const listColumns: ColumnsType<PackageListItem> = [
    { key: 'code', title: 'Mã gói', width: 140, render: (_, p) => <span className="font-mono text-[13px] font-semibold">{p.code}</span> },
    { key: 'name', title: 'Tên gói', width: 230, render: (_, p) => <span><b>{p.name}</b><div className="text-xs text-slate-400">{KIND_LABEL[p.kind]}</div></span> },
    { key: 'items', title: 'Dịch vụ trong gói', width: 300, render: (_, p) => <span className="text-xs text-slate-600">{itemsSummary(p)}</span> },
    { key: 'price', title: <Tooltip title="Giá đang áp dụng tại chi nhánh của bạn">Giá bán hiện tại</Tooltip>, width: 170, align: 'right', render: (_, p) => priceCell(p) },
    { key: 'valid', title: 'Hạn dùng', width: 90, render: (_, p) => `${p.validity_months} tháng` },
    { key: 'sold', title: 'Đã bán', width: 110, align: 'right', render: (_, p) => (p.sold_count ? <span>{p.sold_count}<div className="text-xs text-slate-400">{p.active_cards_count} đang dùng</div></span> : '—') },
    {
      key: 'active', title: 'Đang bán', width: 90,
      render: (_, p) => <Tooltip title={can('edit') ? '' : 'Cần quyền sửa'}><Switch size="small" checked={p.is_active} disabled={!can('edit')} onClick={(_, e) => e.stopPropagation()} onChange={() => toggle(p)} /></Tooltip>,
    },
  ];

  const promoCell = (p: PackageListItem) => {
    const st = p.promo_state;
    if (!p.promo || !st) return <span className="text-slate-300">—</span>;
    return <span><b className="text-green-700">{money(p.promo.price)}</b><div className="text-xs text-slate-500">{fmtDate(p.promo.from)}–{fmtDate(p.promo.to)}</div><Pill {...PROMO_PILL[st]} /></span>;
  };

  // Cột chi nhánh: theo danh sách chi nhánh thật, bổ sung chi nhánh có trong branch_prices nếu thiếu
  const branchCols = useMemo(() => {
    const m = new Map(branches.map(b => [b.id, b.name]));
    rows.forEach(p => p.branch_prices?.forEach(b => { if (!m.has(b.branch_id)) m.set(b.branch_id, b.branch_name || `#${b.branch_id}`); }));
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [branches, rows]);

  const pricingColumns: ColumnsType<PackageListItem> = [
    { key: 'name', title: 'Gói', width: 210, fixed: 'left', render: (_, p) => <span><b>{p.name}</b><div className="font-mono text-xs text-slate-400">{p.code}</div></span> },
    { key: 'list', title: 'Niêm yết', width: 120, align: 'right', render: (_, p) => <b>{money(p.price)}</b> },
    ...branchCols.map(b => ({
      key: `b${b.id}`, title: b.name, width: 120, align: 'right' as const,
      render: (_: unknown, p: PackageListItem) => { const v = p.branch_prices?.find(x => x.branch_id === b.id)?.price; return v ? <b className="text-blue-700">{money(v)}</b> : <span className="text-slate-400">{money(p.price)}</span>; },
    })),
    { key: 'promo', title: 'Khuyến mãi', width: 150, render: (_, p) => promoCell(p) },
    { key: 'refund', title: 'Hoàn tiền', width: 110, render: (_, p) => (p.allow_refund ? `Phí ${Number(p.refund_fee_percent)}%` : <span className="text-red-600">Không hoàn</span>) },
  ];

  return (
    <TreatmentShell
      title="Gói liệu trình"
      subtitle="Quản lý gói và quy định tiền từng gói. Gói đang bán hiện ngay trong danh mục POS; sửa gói không ảnh hưởng thẻ đã bán."
      actions={<>
        {can('export') && <button onClick={exportPricing} disabled={exporting} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:border-[#3e91ab] disabled:opacity-50"><Download className="h-4 w-4" /> {exporting ? 'Đang xuất…' : 'Bảng giá'}</button>}
        {can('edit') && <button onClick={() => setConfigOpen(true)} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:border-[#3e91ab]"><Settings2 className="h-4 w-4" /> Quy tắc chung</button>}
        {can('edit') && <button onClick={() => setEditing({})} className="flex h-9 items-center gap-1.5 rounded-lg bg-[#357f96] px-3 text-sm font-semibold text-white hover:bg-[#2c6b7f]"><Plus className="h-4 w-4" /> Tạo gói</button>}
      </>}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Segmented value={view} onChange={v => setView(v as 'list' | 'pricing')} options={[{ value: 'list', label: 'Danh sách gói' }, { value: 'pricing', label: 'Bảng giá & quy định tiền' }]} />
        <Input.Search allowClear placeholder="Mã, tên gói…" value={q} onChange={e => setQ(e.target.value)} className="w-64" />
        <span className="ml-auto text-sm text-slate-500">{summary.active} gói đang bán / {summary.total}</span>
      </div>
      <Table<PackageListItem> rowKey="id" size="middle" className="custom-ant-table" loading={loading}
        columns={view === 'list' ? listColumns : pricingColumns} dataSource={rows} scroll={{ x: 'max-content' }}
        locale={{ emptyText: <Empty description={search ? 'Không có gói phù hợp' : 'Chưa có gói nào — bấm "Tạo gói" để thêm'} /> }}
        pagination={{ current: page, pageSize: PAGE_SIZE, total, hideOnSinglePage: true, showSizeChanger: false, onChange: setPage }}
        rowClassName={p => (p.is_active ? 'cursor-pointer' : 'cursor-pointer opacity-60')}
        onRow={p => ({ onClick: () => can('edit') && setEditing({ id: p.id }) })} />
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
        {view === 'list' ? <>
          <Pill label={ROLE_LABEL.main} cls="bg-slate-50 text-slate-600 border-slate-200" /> tính tiền, được hoàn ·
          <Pill label={ROLE_LABEL.bonus} cls="bg-green-50 text-green-700 border-green-200" /> tặng kèm, không hoàn tiền · bấm vào gói để sửa
        </> : <>Giá chi nhánh <b className="text-blue-700">màu xanh</b> là giá riêng; màu xám là theo niêm yết. Bấm vào gói để sửa quy định tiền.</>}
      </div>
      <PackageForm target={editing} onClose={() => setEditing(null)} onSaved={load} />
      <ConfigModal open={configOpen} onClose={() => setConfigOpen(false)} />
    </TreatmentShell>
  );
}
